import React, {useState} from 'react';
import {Link} from "react-router-dom";
import {useFormik} from "formik";
import {Typography} from "@material-ui/core";
import {Form, InputGroup, Button} from 'react-bootstrap';
import _ from 'lodash';
import {checkId, sendMobileCert, validMobileCert} from "../../../../redux/modules/auth/crud";
import {makeHasError, makeValidClasses} from "../../../../utils/formHelpers";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  adminId: Yup.string()
    .required("아이디를 입력하세요.")
    .min(3, "3자 이상 입력하세요.")
    .max(50, "50자 이하로 입력하세요.")
    .test({
      name: 'checkId',
      message: '중복된 ID 입니다.',
      test: _.debounce(async (adminId) => {
        const {duplAdminId} = await checkId({adminId});
        return !duplAdminId;
      }, 50),
    }),
  password: Yup.string().required("비밀번호를 입력하세요.").min(8, "8자 이상 입력하세요.").max(50, "50자 이하로 입력하세요."),
  passwordConfirm: Yup.string().required("비밀번호를 한번 더 입력하세요.").when("password", {
    is: (val) => val && val.length > 0,
    then: Yup.string().oneOf([Yup.ref("password")], "입력한 비밀번호와 일치하지 않습니다."),
  }),
  telNo: Yup.number().required("전화번호를 입력하세요."),
  email: Yup.string().email('올바른 형식의 이메일을 입력해주세요.').required("이메일 주소를 입력하세요."),
});

export function Step1(props) {
  const {initValues, goNext} = props;
  const [isSend, setSend] = useState(false);
  const [isValid, setValid] = useState(false);

  const form = useFormik({
    initialValues: initValues,
    validationSchema,
    onSubmit: (values, {setStatus, setSubmitting, ...etc}) => {
      goNext(values);
    },
  });

  const getValidClasses = makeValidClasses(form);
  const hasError = makeHasError(form);

  const handleSendCert = () => {
    const {telNo} = form.values;
    sendMobileCert(telNo).then((res) => {
      setSend(true);
    }).catch(error => {
      alert(error.message);
    });
  };

  const handleValidCert = () => {
    const {authNo, telNo} = form.values;
    validMobileCert({authNo, telNo}).then(() => {
      setValid(true);
      form.setFieldTouched('authNo', true);
    }).catch(err => {
      alert(err.message);
    });
  };

  // const setInitialValues = useCallback((key) => {
  //   form.setFieldTouched(key, true);
  // }, [form])
  //
  // useEffect(() => {
  //   const {adminId, password, passwordConfirm, telNo, email} = initValues;
  //   adminId && setInitialValues('adminId');
  //   password && setInitialValues('password');
  //   passwordConfirm && setInitialValues('passwordConfirm');
  //   telNo && setInitialValues('telNo');
  //   email && setInitialValues('email');
  // }, []);

  return (
    <Form
      id="registration-form-2"
      className="form fv-plugins-bootstrap fv-plugins-framework animated animate__animated animate__backInUp mt-5"
      onSubmit={form.handleSubmit}
    >

      <div className="mb-10">
        <Typography>펜타링크 공급파트너 서비스 이용 시 필요한 기본 정보를 등록해주세요.</Typography>
        <Typography>모든 항목은 <span className="text-danger">*</span>필수 입력입니다.</Typography>
      </div>

      {/* begin: Alert */}
      {form.status && (
        <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
          <div className="alert-text font-weight-bold">{form.status}</div>
        </div>
      )}
      {/* end: Alert */}

      {/* begin: Account */}
      <Form.Group controlId="account">
        <Form.Label>
          <span className="text-danger mr-2 align-middle">*</span>
          <span className="font-weight-boldest">ID</span>
        </Form.Label>
        <Form.Control
          placeholder="아이디"
          type="text"
          name="adminId"
          className={`form-control h-auto py-5 px-6 ${getValidClasses("adminId")}`}
          {...form.getFieldProps("adminId")}
        />
        {hasError('adminId') ? (
          <div className="fv-plugins-message-container">
            <Form.Text className="fv-help-block">
              {form.errors.adminId}
            </Form.Text>
          </div>
        ) : (
          <Form.Text className="text-muted">
            ID를 입력해주세요.
          </Form.Text>
        )}
      </Form.Group>
      {/* end: Account */}

      {/* begin: Password */}
      <Form.Group controlId="password">
        <Form.Label>
          <span className="text-danger mr-2 align-middle">*</span>
          <span className="font-weight-boldest">비밀번호</span>
        </Form.Label>
        <Form.Control
          placeholder="비밀번호"
          type="password"
          name="password"
          className={`form-control h-auto py-5 px-6 ${getValidClasses("password")}`}
          {...form.getFieldProps("password")}
        />
        {hasError('password') ? (
          <div className="fv-plugins-message-container">
            <Form.Text className="fv-help-block">{form.errors.password}</Form.Text>
          </div>
        ) : (
          <Form.Text className="text-muted">
            4~6자리 영문자와 숫자 조합으로 입력해주세요.
          </Form.Text>
        )}
      </Form.Group>
      {/* end: Password */}

      {/* begin: Confirm Password */}
      <Form.Group controlId="passwordConfirm">
        <Form.Label>
          <span className="text-danger mr-2 align-middle">*</span>
          <span className="font-weight-boldest">비밀번호 확인</span>
        </Form.Label>
        <Form.Control
          placeholder="비밀번호 확인"
          type="password"
          name="passwordConfirm"
          className={`form-control h-auto py-5 px-6 ${getValidClasses("passwordConfirm")}`}
          {...form.getFieldProps("passwordConfirm")}
        />
        {hasError('passwordConfirm') ? (
          <div className="fv-plugins-message-container">
            <Form.Text className="fv-help-block">{form.errors.passwordConfirm}</Form.Text>
          </div>
        ) : (
          <Form.Text className="text-muted">
            확인을 위해 비밀번호를 한번 더 입력해주세요.
          </Form.Text>
        )}
      </Form.Group>
      {/* end: Confirm Password */}

      {/* begin: Mobile */}
      <Form.Group controlId="mobile">
        <Form.Label>
          <span className="text-danger mr-2 align-middle">*</span>
          <span className="font-weight-boldest">전화번호</span>
        </Form.Label>
        <InputGroup className="mb-2 mr-sm-2">
          <Form.Control
            placeholder="- 없이 입력"
            type="text"
            name="telNo"
            maxLength={11}
            className={`form-control h-auto py-5 px-6 ${getValidClasses("telNo", isValid)}`}
            {...form.getFieldProps("telNo")}
          />
          <InputGroup.Append>
            <Button
              disabled={form.values.telNo.length !== 11}
              variant="primary"
              className="w-120px font-weight-boldest font-size-h6"
              onClick={handleSendCert}
            >
              {isSend ? '재전송' : '인증번호 전송'}
            </Button>
          </InputGroup.Append>
        </InputGroup>
        {hasError('telNo') ? (
          <div className="fv-plugins-message-container">
            <Form.Text className="fv-help-block">{form.errors.telNo}</Form.Text>
          </div>
        ) : (
          <Form.Text className="text-muted">
            가입 신청 결과를 입력하신 번호로 알려드립니다.<br/>
            인증번호 전송은 통신사에 따라 최대 1분까지 소요될 수 있습니다.<br/>
            인증번호가 도착하지 않을 경우 ‘재전송’을 눌러주세요.
          </Form.Text>
        )}
      </Form.Group>
      {/* end: Mobile */}

      {/* begin: Mobile Cert */}
      {isSend && (
        <Form.Group controlId="mobileCert">
          <InputGroup className="mb-2 mr-sm-2">
            <Form.Control
              placeholder="인증번호"
              type="text"
              name="authNo"
              className={`form-control h-auto py-5 px-6 ${getValidClasses("authNo", isValid)}`}
              {...form.getFieldProps("authNo")}
            />
            <InputGroup.Append>
              <Button
                disabled={!form.values.authNo}
                variant="primary"
                className="w-120px font-weight-boldest font-size-h6"
                onClick={handleValidCert}
              >
                인증
              </Button>
            </InputGroup.Append>
          </InputGroup>
          {hasError('authNo') ? (
            <div className="fv-plugins-message-container">
              <Form.Text className="fv-help-block">{form.errors.mobileCert}</Form.Text>
            </div>
          ) : null}
        </Form.Group>
      )}
      {/* end: Mobile Cert */}

      {/* begin: Email */}
      <Form.Group controlId="email">
        <Form.Label>
          <span className="text-danger mr-2 align-middle">*</span>
          <span className="font-weight-boldest">이메일 주소</span>
        </Form.Label>
        <Form.Control
          placeholder="이메일 주소"
          type="email"
          name="email"
          className={`form-control h-auto py-5 px-6 ${getValidClasses("email")}`}
          {...form.getFieldProps("email")}
        />
        {hasError('email') ? (
          <div className="fv-plugins-message-container">
            <Form.Text className="fv-help-block">{form.errors.email}</Form.Text>
          </div>
        ) : null}
      </Form.Group>
      {/* end: Email */}

      {/* end: Terms and Conditions */}
      <div className="form-group d-flex flex-wrap flex-center">
        <div className="w-50 px-4 my-3">
          <Link to="/auth/login">
            <button type="button" className="btn btn-secondary font-weight-bold px-9 py-4 w-100">
              다음에 할게요
            </button>
          </Link>
        </div>
        <div className="w-50 px-4 my-3">
          <button
            type="submit"
            disabled={form.isSubmitting || !form.isValid}
            className="btn btn-primary font-weight-bold px-9 py-4 w-100"
          >
            <span>다음 ></span>
          </button>
        </div>
      </div>
    </Form>
  )
}
